import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { Slot } from '@tutlo/contracts'
import { Button } from '@tutlo/ui'
import { useEffect, useState } from 'react'
import { AskFooter, Shell } from '../layout.tsx'
import {
  ApiError,
  sendClientEvent,
  useCreateBooking,
  useLead,
  useReschedule,
  useSlots,
} from '../lead.ts'
import { DayPicker } from '../screens/booking/day-picker.tsx'
import { diverseSlots, SlotChoice } from '../screens/booking/slot-choice.tsx'
import { SlotSummaryPanel } from '../screens/booking/slot-summary-panel.tsx'
import { validateSearch } from '../search.ts'
import { formatTime, minutesUntil, relativeDay } from '../time.ts'

/** Terminy max 3 dni w przód (Matt, 2026-09-04): push na „dziś / jutro”, nie planowanie za tydzień. */
export const SLOT_DAYS = 3
/** How many diversified nearest slots the choice screen offers (picking one skips the day picker). */
const NEAREST_CHIPS = 3
/** Pasek dni pokazuje tydzień; dni poza oknem `SLOT_DAYS` nie mają slotów, więc są zablokowane (Matt, 2026-09-04). */
const SHOWN_DAYS = 7

export const Route = createFileRoute('/kalendarz')({
  validateSearch,
  component: CalendarRoute,
})

/**
 * Slots starting sooner than this vanish from the UI — "za 2 min" is bookable but not joinable
 * calmly (Robert, 2026-09-12). The list re-filters on a clock tick, so a slot expiring while the
 * lead stares at the screen slides away (and its selection resets) instead of going stale.
 */
const MIN_BUFFER_MIN = 15

/** "Rezerwuję dziś 16:30 →" — no countdown here: a minutes-based CTA ages every minute. */
const bookLabel = (slot: Slot) =>
  `Rezerwuję ${relativeDay(slot.start).toLowerCase()} ${formatTime(slot.start)} →`

/**
 * Default view: active slot choice (replaced the Figma countdown tile `181:644` — it optimized
 * bookings, not show-ups); day picker `181:1398` under `?widok=dni`.
 * Slot wybrany tutaj jedzie w URL do `/telefon` (umawiator nie ma holdu, D3); lead, który podał już
 * telefon (rebooking z poczekalni po nieodbytym / odwołanym spotkaniu), rezerwuje od razu stąd.
 * `?zmiana=<bookingId>` (D8): od razu picker, wybór = `POST reschedule` → `/s/:token`. Od 2026-09-09 nie
 * linkuje tu żaden ekran — przekładanie terminu prowadzi agent w czacie; trasa zostaje jako ścieżka awaryjna.
 */
function CalendarRoute() {
  const search = Route.useSearch()
  const navigate = useNavigate()
  const lead = useLead()
  const slots = useSlots(SLOT_DAYS)
  const [selected, setSelected] = useState<Slot | null>(null)
  const changing = search.zmiana ?? ''
  const reschedule = useReschedule(changing)
  const createBooking = useCreateBooking()
  const [changeError, setChangeError] = useState<string | null>(null)

  // Clock tick: re-runs the MIN_BUFFER_MIN filter and refreshes "za X min" labels while idle.
  const [, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 30_000)
    return () => clearInterval(id)
  }, [])

  const leadId = lead.data?.id
  useEffect(() => {
    if (leadId)
      sendClientEvent(leadId, {
        name: 'calendar_shown',
        props: { view: search.widok ?? 'kafelek' },
      })
  }, [leadId, search.widok])

  if (lead.isPending || slots.isPending) return <Shell nav={{}}>{null}</Shell>
  if (!lead.data) {
    navigate({ to: '/', replace: true })
    return null
  }
  if (!lead.data.email) {
    navigate({ to: '/konto', replace: true })
    return null
  }

  const all = (slots.data?.slots ?? []).filter((s) => minutesUntil(s.start) >= MIN_BUFFER_MIN)
  const nearest = all[0] ?? null
  // A picked slot that has meanwhile slipped under the buffer is no longer offerable.
  const valid = selected && minutesUntil(selected.start) >= MIN_BUFFER_MIN ? selected : null
  const durationMin = slots.data?.durationMin ?? 30
  const startToken = lead.data.startToken
  const toStart = () => navigate({ to: '/s/$token', params: { token: startToken } })
  const tiles = diverseSlots(all, NEAREST_CHIPS)
  const pick = (slot: Slot) => {
    const view = changing ? 'zmiana' : (search.widok ?? 'kafelek')
    sendClientEvent(lead.data?.id ?? '', {
      name: 'slot_selected',
      props: {
        start: slot.start,
        minutes_until: minutesUntil(slot.start),
        view,
        // Do leads take the slot we suggest, and does it show up in attendance (Tutlo, 2026-10-14)?
        // rank 0 = the nearest offered slot; tile = which of the suggested cards; preselected = the
        // calendar's default (nearest) booked without picking another.
        rank: all.findIndex((s) => s.start === slot.start),
        ...(view === 'kafelek' ? { tile: tiles.findIndex((s) => s.start === slot.start) } : {}),
        ...(view !== 'kafelek' ? { preselected: !valid } : {}),
      },
    })
    if (changing) {
      void change(slot)
      return
    }
    if (lead.data?.phone) {
      void book(slot)
      return
    }
    navigate({ to: '/telefon', search: { start: slot.start } })
  }
  const change = async (slot: Slot) => {
    setChangeError(null)
    try {
      await reschedule.mutateAsync({ start: slot.start })
      toStart()
    } catch (err) {
      setChangeError(
        err instanceof ApiError
          ? err.message
          : 'Nie udało się zmienić terminu. Spróbuj jeszcze raz.',
      )
    }
  }
  const book = async (slot: Slot) => {
    setChangeError(null)
    try {
      await createBooking.mutateAsync({ leadId: lead.data?.id ?? '', start: slot.start })
      toStart()
    } catch (err) {
      setChangeError(
        err instanceof ApiError ? err.message : 'Nie udało się zarezerwować. Spróbuj jeszcze raz.',
      )
    }
  }

  if (slots.isError || all.length === 0) {
    return (
      <Shell nav={{}} footer={<AskFooter />}>
        <div className="flex flex-col gap-base pt-base">
          <h1 className="text-h2">Umów lekcję próbną</h1>
          <p className="text-body text-text-secondary">
            {slots.isError
              ? 'Nie udało się pobrać terminów. Spróbuj za chwilę.'
              : 'W najbliższych dniach nie ma wolnych terminów. Sprawdź za chwilę albo napisz na czacie.'}
          </p>
          <Button variant="secondary" onClick={() => slots.refetch()}>
            Odśwież terminy
          </Button>
        </div>
      </Shell>
    )
  }

  if (search.widok !== 'dni' && nearest && !changing) {
    // No preselection: the CTA unlocks only after an explicit tap — the tap is the
    // micro-commitment this screen buys show-up with (booking ≠ showing up).
    const picked = valid
    return (
      <Shell
        nav={{}}
        footer={<AskFooter />}
        aside={
          <SlotSummaryPanel
            slot={picked ?? nearest}
            durationMin={durationMin}
            badge={picked ? undefined : 'Najbliższy wolny termin'}
          />
        }
        cta={
          <div className="flex flex-col gap-sm">
            {changeError ? (
              <p
                role="alert"
                className="rounded-card-sm bg-warning-soft px-[14px] py-md text-label text-warning-text"
              >
                {changeError}
              </p>
            ) : null}
            <Button
              disabled={!picked}
              loading={createBooking.isPending}
              onClick={() => picked && pick(picked)}
            >
              {picked ? bookLabel(picked) : 'Zaznacz godzinę powyżej'}
            </Button>
          </div>
        }
      >
        <div className="flex flex-1 flex-col pt-base pb-base">
          <SlotChoice
            slots={tiles}
            selected={picked}
            onSelect={setSelected}
            durationMin={durationMin}
            onOther={() => navigate({ to: '/kalendarz', search: { widok: 'dni' } })}
          />
        </div>
      </Shell>
    )
  }

  const current = valid ?? nearest
  const ctaLabel = current
    ? changing
      ? `Zmieniam na ${relativeDay(current.start).toLowerCase()}, ${formatTime(current.start)}`
      : bookLabel(current)
    : 'Wybierz termin'
  return (
    <Shell
      nav={{ back: () => (changing ? toStart() : navigate({ to: '/kalendarz', search: {} })) }}
      aside={current ? <SlotSummaryPanel slot={current} durationMin={durationMin} /> : undefined}
      fitViewport
      cta={
        <div className="flex flex-col gap-sm">
          {changeError ? (
            <p
              role="alert"
              className="rounded-card-sm bg-warning-soft px-[14px] py-md text-label text-warning-text"
            >
              {changeError}
            </p>
          ) : null}
          <Button
            disabled={!current}
            loading={reschedule.isPending || createBooking.isPending}
            onClick={() => current && pick(current)}
          >
            {ctaLabel}
          </Button>
        </div>
      }
    >
      <div className="flex min-h-0 flex-1 flex-col pt-sm tablet:pt-base">
        <DayPicker
          slots={all}
          selected={current}
          onSelect={setSelected}
          days={SHOWN_DAYS}
          openDays={SLOT_DAYS}
        />
      </div>
    </Shell>
  )
}
